import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { 
  Plus, Edit, Trash, X, PieChart, Filter, Loader, GripVertical, 
  Search, Settings, Users, TrendingUp, Building
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Customer } from '../lib/customers';
import SelectCustomerModal from '../components/manage/SelectCustomerModal';
import { useUnreadOpportunitiesContext } from '../contexts/UnreadOpportunitiesContext';

interface Stage {
  id: number;
  name: string;
  position?: number;
}

interface Pipeline {
  id: number;
  business_id: string;
  pipeline_name: string;
  stages?: Stage[];
}

interface Opportunity {
  id: number;
  business_id: string;
  customer_id: string;
  title: string;
  pipeline_id: number;
  stage_id: number;
  value: number;
  business_name?: string | null;
  source?: string | null;
  tags?: string | null;
  status: string;
  created_at: string;
  custom_fields?: string | null; // JSON string of custom fields
  is_unread?: boolean; // Indicates if the opportunity is unread (shows red dot indicator)
  customer_name?: string; // Customer name for display
  customers?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

export default function CRM() {
  const { selectedBusiness } = useBusiness();
  const { markAsRead, incrementCount } = useUnreadOpportunitiesContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('opportunities');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentPipelineId, setCurrentPipelineId] = useState<number | null>(null);
  const [pipelineName, setPipelineName] = useState('');
  const [stages, setStages] = useState<Stage[]>([{ id: 1, name: '', position: 1 }]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draggedStageId, setDraggedStageId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'list'>('all');
  const [isOpportunityModalOpen, setIsOpportunityModalOpen] = useState(false);
  const [isOpportunityInfoModalOpen, setIsOpportunityInfoModalOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isCustomFieldsModalOpen, setIsCustomFieldsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [opportunityTitle, setOpportunityTitle] = useState('');
  const [selectedOpportunityForInfo, setSelectedOpportunityForInfo] = useState<Opportunity | null>(null);
  const [opportunitiesViewPipeline, setOpportunitiesViewPipeline] = useState<Pipeline | null>(null);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [opportunityValue, setOpportunityValue] = useState(0);
  const [businessName, setBusinessName] = useState('');
  const [opportunitySource, setOpportunitySource] = useState('');
  const [opportunityStatus, setOpportunityStatus] = useState('Open');
  const [availableTags, setAvailableTags] = useState<Array<{id: string, name: string}>>([]);
  const [selectedTags, setSelectedTags] = useState<Array<{id: string, name: string}>>([]);
  const [isNewTagModalOpen, setIsNewTagModalOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [customFields, setCustomFields] = useState<Array<{id: string, name: string, type: string, value: string}>>([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<Array<{id: string, name: string, type: string}>>([]);
  
  // Status filter state
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['Open']); // Default to Open status only
  const [availableStatuses] = useState<string[]>(['Open', 'Won', 'Lost']);
  const [isLoadingStatusFilter, setIsLoadingStatusFilter] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{top: number, left: number, width: number} | null>(null);
  const statusButtonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  
  // Mobile kanban swipe state
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);

  // Function to fetch pipelines from the database
  const fetchPipelines = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    const supabaseClient = supabase; // Create local reference for type safety

    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabaseClient
        .from('pipelines')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('pipeline_name', { ascending: true });

      if (error) {
        throw error;
      }
      
      const fetchedPipelines = data || [];
      
      // Transform pipelines to include parsed stages (stages are stored as JSON in the stages column)
      const pipelinesWithStages = fetchedPipelines.map(pipeline => ({
        ...pipeline,
        stages: Array.isArray(pipeline.stages) ? pipeline.stages : []
      }));

      setPipelines(pipelinesWithStages);
      
    } catch (err) {
      console.error('Error fetching pipelines:', err);
      setError('Failed to load pipelines. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch opportunities from the database
  const fetchOpportunities = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      // First, try to fetch opportunities with customer data
      let { data, error } = await supabase
        .from('pipeline_opportunities')
        .select(`
          *,
          customers(
            id,
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      // If the join fails, fallback to just opportunities
      if (error) {
        console.warn('Customer join failed, fetching opportunities only:', error);
        const fallbackResult = await supabase
          .from('pipeline_opportunities')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false });
          
        data = fallbackResult.data;
        error = fallbackResult.error;
      }

      if (error) throw error;
      
      console.log('Raw opportunity data:', data);
      
      // Map the data to include customer_name
      const opportunitiesWithCustomerNames = await Promise.all(
        (data || []).map(async (opportunity) => {
          console.log('Processing opportunity:', opportunity);
          
          let customerName = 'Unknown Contact';
          
          // If we have customer data from the join
          if (opportunity.customers) {
            console.log('Customer data from join:', opportunity.customers);
            customerName = `${opportunity.customers.first_name || ''} ${opportunity.customers.last_name || ''}`.trim();
          } 
          // If no customer data from join, fetch it separately
          else if (opportunity.customer_id) {
            try {
              const { data: customerData } = await supabase
                .from('customers')
                .select('first_name, last_name')
                .eq('id', opportunity.customer_id)
                .single();
                
              if (customerData) {
                customerName = `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim();
              }
            } catch (customerError) {
              console.warn('Failed to fetch customer for opportunity:', opportunity.id, customerError);
            }
          }
          
          console.log('Generated customer name:', customerName);
          
          return {
            ...opportunity,
            customer_name: customerName || 'Unknown Contact'
          };
        })
      );
      
      console.log('Final opportunities:', opportunitiesWithCustomerNames);
      setOpportunities(opportunitiesWithCustomerNames);
      
    } catch (err) {
      console.error('Error fetching opportunities:', err);
      // Don't clear opportunities on error, keep existing ones
    }
  };

  // Function to fetch available tags
  const fetchTags = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('id, name')
        .eq('business_id', selectedBusiness.id)
        .order('name');

      if (error) {
        console.error('Error fetching tags:', error);
        return;
      }

      setAvailableTags(data || []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Function to create a new tag
  const createNewTag = async (name: string) => {
    if (!selectedBusiness?.id || !supabase) return null;
    
    try {
      const { data, error } = await supabase
        .from('tags')
        .insert({
          business_id: selectedBusiness.id,
          name: name.trim()
        })
        .select('id, name')
        .single();

      if (error) {
        console.error('Error creating tag:', error);
        return null;
      }

      // Add to available tags
      setAvailableTags(prev => [...prev, data]);
      return data;
    } catch (error) {
      console.error('Error creating tag:', error);
      return null;
    }
  };

  // Function to handle adding a new tag from the modal
  const handleAddNewTag = async () => {
    if (!newTagName.trim()) return;
    
    const newTag = await createNewTag(newTagName);
    if (newTag) {
      setSelectedTags(prev => [...prev, newTag]);
      setNewTagName('');
      setIsNewTagModalOpen(false);
    }
  };

  // Function to handle tag selection
  const handleTagSelect = (tag: {id: string, name: string}) => {
    if (!selectedTags.find(t => t.id === tag.id)) {
      setSelectedTags(prev => [...prev, tag]);
    }
  };

  // Function to remove a selected tag
  const handleTagRemove = (tagId: string) => {
    setSelectedTags(prev => prev.filter(t => t.id !== tagId));
  };

  // Load custom field definitions from database
  const loadCustomFieldDefinitions = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      console.log('🔍 Loading custom field definitions from database...');
      const { data, error } = await supabase
        .from('opportunity_custom_fields')
        .select('id, field_name, field_type, display_order')
        .eq('business_id', selectedBusiness.id)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('❌ Error loading custom field definitions:', error);
        return;
      }

      // Transform database format to component format
      const definitions = (data || []).map(field => ({
        id: field.id,
        name: field.field_name,
        type: field.field_type
      }));

      console.log('✅ Loaded custom field definitions:', definitions);
      setCustomFieldDefinitions(definitions);
    } catch (error) {
      console.error('❌ Error loading custom field definitions:', error);
    }
  };

  // Initialize custom fields based on definitions
  const initializeCustomFields = () => {
    const initialFields = customFieldDefinitions.map(def => ({
      id: def.id,
      name: def.name,
      type: def.type,
      value: ''
    }));
    setCustomFields(initialFields);
  };

  // Function to save pipeline status filters to database
  const savePipelineStatusFilters = async (pipelineId: number, statuses: string[]) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      setIsLoadingStatusFilter(true);
      
      const { error } = await supabase
        .from('pipeline_status_filters')
        .upsert({
          business_id: selectedBusiness.id,
          pipeline_id: pipelineId,
          selected_statuses: statuses
        }, {
          onConflict: 'business_id,pipeline_id'
        });
      
      if (error) {
        console.error('Error saving pipeline status filters:', error);
        return;
      }
      
      console.log('Pipeline status filters saved successfully');
    } catch (error) {
      console.error('Error saving pipeline status filters:', error);
    } finally {
      setIsLoadingStatusFilter(false);
    }
  };
  
  // Function to load pipeline status filters from database
  const loadPipelineStatusFilters = async (pipelineId: number) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('pipeline_status_filters')
        .select('selected_statuses')
        .eq('business_id', selectedBusiness.id)
        .eq('pipeline_id', pipelineId)
        .single();
      
      if (error) {
        // If no filters exist, default to Open status only
        if (error.code === 'PGRST116') {
          setSelectedStatuses(['Open']);
          return;
        }
        console.error('Error loading pipeline status filters:', error);
        return;
      }
      
      if (data?.selected_statuses) {
        setSelectedStatuses(data.selected_statuses);
      }
    } catch (error) {
      console.error('Error loading pipeline status filters:', error);
    }
  };
  

  
  // Load status filters when pipeline changes
  useEffect(() => {
    if (opportunitiesViewPipeline) {
      loadPipelineStatusFilters(opportunitiesViewPipeline.id);
    }
  }, [opportunitiesViewPipeline]);

  // Close status filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isStatusFilterOpen && !target.closest('.relative')) {
        setIsStatusFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusFilterOpen]);

  // Initialize custom fields when definitions change
  useEffect(() => {
    initializeCustomFields();
  }, [customFieldDefinitions]);

  // Load pipelines, opportunities, and custom field definitions when the component mounts or business changes
  useEffect(() => {
    if (selectedBusiness?.id) {
      fetchPipelines();
      fetchOpportunities();
      fetchTags();
      loadCustomFieldDefinitions(); // This is now async but we don't need to await it
    }
  }, [selectedBusiness?.id]);

  // Set default pipeline for opportunities view when pipelines are loaded
  useEffect(() => {
    if (pipelines.length > 0 && !opportunitiesViewPipeline) {
      setOpportunitiesViewPipeline(pipelines[0]);
    }
  }, [pipelines, opportunitiesViewPipeline]);

  const addStage = () => {
    const newPosition = stages.length + 1;
    setStages([...stages, { id: Date.now(), name: '', position: newPosition }]);
  };

  const updateStageName = (id: number, name: string) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, name } : stage
    ));
  };

  const removeStage = (id: number) => {
    if (stages.length > 1) {
      const filteredStages = stages.filter(stage => stage.id !== id);
      // Reorder positions after removing
      const updatedStages = filteredStages.map((stage, index) => ({
        ...stage,
        position: index + 1
      }));
      setStages(updatedStages);
    }
  };

  const handleDragStart = (e: React.DragEvent, id: number) => {
    setDraggedStageId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Set a ghost image for dragging (optional)
    const draggedElement = e.currentTarget as HTMLElement;
    if (draggedElement) {
      const ghost = draggedElement.cloneNode(true) as HTMLElement;
      ghost.style.position = 'absolute';
      ghost.style.top = '-1000px';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 0, 0);
      setTimeout(() => {
        document.body.removeChild(ghost);
      }, 0);
    }
  };

  const handleDragOver = (e: React.DragEvent, id: number) => {
    e.preventDefault();
    if (draggedStageId === null || draggedStageId === id) return;

    // Find positions of the dragged and target stages
    const draggedStage = stages.find(stage => stage.id === draggedStageId);
    const targetStage = stages.find(stage => stage.id === id);
    
    if (!draggedStage || !targetStage) return;
    
    // Create a new array with reordered stages
    const draggedPosition = draggedStage.position || 0;
    const targetPosition = targetStage.position || 0;
    
    const newStages = stages.map(stage => {
      if (stage.id === draggedStageId) {
        return { ...stage, position: targetPosition };
      } else if (
        (draggedPosition < targetPosition && 
          stage.position && 
          stage.position > draggedPosition && 
          stage.position <= targetPosition)
      ) {
        return { ...stage, position: (stage.position || 0) - 1 };
      } else if (
        (draggedPosition > targetPosition && 
          stage.position && 
          stage.position < draggedPosition && 
          stage.position >= targetPosition)
      ) {
        return { ...stage, position: (stage.position || 0) + 1 };
      }
      return stage;
    });
    
    // Sort by position and update
    setStages([...newStages].sort((a, b) => (a.position || 0) - (b.position || 0)));
  };

  const handleDragEnd = () => {
    setDraggedStageId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBusiness?.id || !pipelineName.trim() || !supabase) {
      return;
    }
    
    // Validate that at least one stage has a name
    const validStages = stages.filter(stage => stage.name.trim() !== '');
    if (validStages.length === 0) {
      alert('Please add at least one stage with a name');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Format stages for storage - ensure stages have position property
      const formattedStages = validStages.map((stage, index) => ({
        id: stage.id,
        name: stage.name.trim(),
        position: stage.position || index + 1
      }));
      
      // Data to insert/update
      const pipelineData = { 
        business_id: selectedBusiness.id,
        pipeline_name: pipelineName.trim(),
        stages: formattedStages
      };
      
      let error;
      
      if (isEditMode && currentPipelineId) {
        // Update existing pipeline
        const { error: updateError } = await supabase
          .from('pipelines')
          .update(pipelineData)
          .eq('id', currentPipelineId);
        
        error = updateError;
      } else {
        // Create new pipeline
        const { error: insertError } = await supabase
          .from('pipelines')
          .insert([pipelineData])
          .select();
        
        error = insertError;
      }
      
      if (error) throw error;
      
      // Refresh the pipelines list
      fetchPipelines();
      
      // Close modal and reset form
      handleCloseModal();
      
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} pipeline:`, err);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} pipeline. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (editMode: boolean = false, pipeline?: Pipeline) => {
    setIsEditMode(editMode);
    
    if (editMode && pipeline) {
      // Load pipeline data for editing
      setCurrentPipelineId(pipeline.id);
      setPipelineName(pipeline.pipeline_name);
      
      // Load stages if they exist, otherwise create a default one
      if (pipeline.stages && pipeline.stages.length > 0) {
        setStages(pipeline.stages.map(stage => ({
          ...stage,
          id: typeof stage.id === 'number' ? stage.id : Date.now() + Math.floor(Math.random() * 1000)
        })));
      } else {
        setStages([{ id: Date.now(), name: '', position: 1 }]);
      }
    } else {
      // Reset for create mode
      setCurrentPipelineId(null);
      setPipelineName('');
      setStages([{ id: 1, name: '', position: 1 }]);
    }
    
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentPipelineId(null);
    setPipelineName('');
    setStages([{ id: 1, name: '', position: 1 }]);
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    setCustomFields([]);
  };

  // Function to delete a pipeline
  const handleDeletePipeline = async (id: number) => {
    if (!supabase || !window.confirm('Are you sure you want to delete this pipeline?')) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('pipelines')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      // Refresh the list
      fetchPipelines();
      
    } catch (err) {
      console.error('Error deleting pipeline:', err);
      alert('Failed to delete the pipeline. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // This function will render the kanban board stage columns
  const renderStageColumns = () => {
    // If no pipelines exist at all, show "create pipeline" message
    if (pipelines.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Pipelines Created</h3>
            <p className="text-gray-500 mb-4">Create a pipeline to start managing your opportunities.</p>
            <button 
              onClick={() => setActiveTab('pipelines')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Create Your First Pipeline
            </button>
          </div>
        </div>
      );
    }

    // If pipeline exists but has no stages, show empty stage message
    if (!opportunitiesViewPipeline || !opportunitiesViewPipeline.stages || opportunitiesViewPipeline.stages.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Stages in Pipeline</h3>
            <p className="text-gray-500 mb-4">This pipeline doesn't have any stages. Edit the pipeline to add stages.</p>
            <button 
              onClick={() => setActiveTab('pipelines')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Edit Pipeline
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex space-x-4 overflow-x-auto pb-4">
        {opportunitiesViewPipeline.stages.map(stage => {
          // Filter opportunities for this stage and selected statuses
          const stageOpportunities = opportunities.filter(opp => 
            opp.pipeline_id === opportunitiesViewPipeline.id && 
            opp.stage_id === stage.id &&
            selectedStatuses.includes(opp.status)
          );
          
          // Calculate total value for this stage
          const totalValue = stageOpportunities.reduce((sum, opp) => sum + (opp.value || 0), 0);
          
          return (
            <div key={stage.id} className="bg-gray-100 rounded-md p-3 min-w-[300px] flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-700">{stage.name}</h3>
                <div className="text-xs text-gray-500">
                  {stageOpportunities.length} Opportunities <span className="ml-2">C${totalValue.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Opportunity Cards */}
              <div className="space-y-3">
                {stageOpportunities.map(opportunity => (
                  <div 
                    key={opportunity.id} 
                    className="bg-white border border-gray-200 rounded-md p-3 relative cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
                    onClick={() => handleOpenOpportunityInfo(opportunity)}
                  >
                    {/* Unread indicator */}
                    {opportunity.is_unread && (
                      <div className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white z-10"></div>
                    )}
                    
                    {/* Delete button */}
                    <button 
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs hover:bg-red-800 transition-colors"
                      onClick={async (e) => {
                        e.stopPropagation(); // Prevent triggering the card click
                        // Mark as read when interacting with the opportunity
                        if (opportunity.is_unread) {
                          await markOpportunityAsRead(opportunity.id);
                        }
                        handleDeleteOpportunityFromCard(opportunity);
                      }}
                      title="Delete opportunity"
                      disabled={isLoading}
                    >
                      ×
                    </button>
                    
                    {/* Opportunity Title */}
                    <h4 className="font-medium text-gray-900 mb-3 pr-8">{opportunity.title}</h4>
                    
                    {/* Opportunity Details */}
                    <div className="space-y-2 mb-3">
                      <div className="flex text-sm">
                        <span className="text-blue-600 w-32">Opportunity Source:</span>
                        <span className="text-gray-600">{opportunity.source || 'Direct'}</span>
                      </div>
                      <div className="flex text-sm">
                        <span className="text-blue-600 w-32">Opportunity Value:</span>
                        <span className="text-gray-600">C${(opportunity.value || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    
                    {/* Action Icons */}
                    <div className="flex space-x-2 pt-2 border-t border-gray-100">
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Call"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Message"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-blue-600 hover:text-blue-800" 
                        title="Favorite"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Document"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Email"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Calendar"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderNoOpportunitiesState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-40 h-40 mb-6">
        <img 
          src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDE2MCAxNjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgb3BhY2l0eT0iMC44Ij4KICAgIDxjaXJjbGUgY3g9IjgwIiBjeT0iODAiIHI9IjgwIiBmaWxsPSIjRjVGNUY1Ii8+CiAgICA8cGF0aCBkPSJNMTE3LjQ0OSA0Mi41NTExQzEyMS4zNjMgNDYuNDY1MSAxMjEuMzYzIDUyLjc5ODIgMTE3LjQ0OSA1Ni43MTIyTDU2LjcxMjIgMTE3LjQ0OUM1Mi43OTgyIDEyMS4zNjMgNDYuNDY1MSAxMjEuMzYzIDQyLjU1MTEgMTE3LjQ0OUMzOC42MzcxIDExMy41MzUgMzguNjM3MSAxMDcuMjAyIDQyLjU1MTEgMTAzLjI4OEwxMDMuMjg4IDQyLjU1MTFDMTAzLjI4OCA0Mi41NTExIDEwNy4yMDIgMzguNjM3MSAxMTcuNDQ5IDQyLjU1MTFaIiBzdHJva2U9IiNCRUJFQkUiIHN0cm9rZS13aWR0aD0iMiIvPgogICAgPHJlY3QgeD0iNjUiIHk9IjY1IiB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHJ4PSIxIiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSIjQkVCRUJFIiBzdHJva2Utd2lkdGg9IjIiLz4KICAgIDxjaXJjbGUgY3g9IjgwIiBjeT0iODAiIHI9IjEwIiBzdHJva2U9IiNGRjZCNkIiIHN0cm9rZS13aWR0aD0iMiIvPgogICAgPHBhdGggZD0iTTc1IDgwTDc4IDgzTDg1IDc2IiBzdHJva2U9IiNGRjZCNkIiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CiAgPC9nPgo8L3N2Zz4K" 
          alt="No opportunities found" 
          className="w-full h-full"
        />
      </div>
      <h3 className="text-lg font-medium text-gray-700">No Opportunities Match the Filter's Set</h3>
      <p className="text-gray-500 text-center mt-2 max-w-md">
        Try adjusting your filters to find the Opportunities you're looking for.
      </p>
      <div className="flex mt-4 space-x-3">
        <button className="flex items-center bg-blue-500 text-white px-4 py-2 rounded-md">
          <Filter size={16} className="mr-2" /> Update Filter
        </button>
        <button className="flex items-center bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md">
          <X size={16} className="mr-2" /> Clear Filter
        </button>
      </div>
    </div>
  );

  // Handle customer selection and set default title
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setOpportunityTitle(customer.first_name); // Set default title to customer's first name
    setIsCustomerModalOpen(false);
  };

  // Handle customer removal
  const handleCustomerRemove = () => {
    setSelectedCustomer(null);
    setOpportunityTitle(''); // Clear title when customer is removed
  };

  // Handle pipeline selection and clear stage
  const handlePipelineSelect = (pipeline: Pipeline | null) => {
    setSelectedPipeline(pipeline);
    setSelectedStage(null); // Clear stage when pipeline changes
  };

  // Handle opening modal with current pipeline context
  const handleOpenOpportunityModal = (currentPipeline?: Pipeline) => {
    setIsOpportunityModalOpen(true);
    
    // Initialize custom fields based on definitions
    initializeCustomFields();
    
    if (currentPipeline) {
      setSelectedPipeline(currentPipeline);
      // Auto-select first stage if available
      if (currentPipeline.stages && currentPipeline.stages.length > 0) {
        setSelectedStage(currentPipeline.stages[0]);
      }
    } else if (pipelines.length > 0) {
      // Default to first pipeline if no specific pipeline context
      const firstPipeline = pipelines[0];
      setSelectedPipeline(firstPipeline);
      // Auto-select first stage if available
      if (firstPipeline.stages && firstPipeline.stages.length > 0) {
        setSelectedStage(firstPipeline.stages[0]);
      }
    }
  };

  // Handle creating a new opportunity (now with database)
  const handleCreateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Create opportunity clicked');
    console.log('Form values:', {
      selectedCustomer,
      selectedPipeline,
      selectedStage,
      opportunityTitle,
      opportunityValue,
      businessName,
      opportunitySource,
      customFields
    });
    
    if (!selectedCustomer || !selectedPipeline || !selectedStage || !selectedBusiness?.id || !supabase) {
      console.log('Validation failed:', {
        hasCustomer: !!selectedCustomer,
        hasPipeline: !!selectedPipeline,
        hasStage: !!selectedStage,
        hasBusinessId: !!selectedBusiness?.id,
        hasSupabase: !!supabase
      });
      return;
    }

    try {
      console.log('Creating opportunity in database...');
      setIsLoading(true);
      
      const opportunityData = {
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        title: opportunityTitle,
        pipeline_id: selectedPipeline.id,
        stage_id: selectedStage.id,
        value: opportunityValue,
        business_name: businessName || null,
        source: opportunitySource || null,
        tags: selectedTags.length > 0 ? JSON.stringify(selectedTags) : null,
        status: opportunityStatus,
        custom_fields: customFields.length > 0 ? JSON.stringify(customFields) : null
      };
      
      console.log('Opportunity data to insert:', opportunityData);
      
      // Create the opportunity in database
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .insert(opportunityData)
        .select()
        .single();

      console.log('Supabase response:', { data, error });

      if (error) throw error;

      // Add the new opportunity to the local state
      setOpportunities(prev => [...prev, data]);
      console.log('Opportunity created successfully:', data);
      
      // Increment unread count since new opportunities default to unread
      incrementCount();
      
      // Close the modal and reset
      handleModalClose();
      
    } catch (err) {
      console.error('Error creating opportunity:', err);
      setError('Failed to create opportunity. Please try again.');
      alert('Error creating opportunity: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle adding a custom field
  const handleAddCustomField = async (name: string, type: string) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      console.log('➕ Adding custom field to database:', { name, type });
      
      // Insert into database
      const { data, error } = await supabase
        .from('opportunity_custom_fields')
        .insert({
          business_id: selectedBusiness.id,
          field_name: name,
          field_type: type,
          display_order: customFieldDefinitions.length
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error adding custom field:', error);
        alert('Failed to add custom field. Please try again.');
        return;
      }

      console.log('✅ Custom field added to database:', data);

      // Transform database format to component format
      const newFieldDef = {
        id: data.id,
        name: data.field_name,
        type: data.field_type
      };
      
      // Add to local state
      setCustomFieldDefinitions(prev => [...prev, newFieldDef]);
      
      // Add to current fields with empty value
      const newField = {
        id: newFieldDef.id,
        name: newFieldDef.name,
        type: newFieldDef.type,
        value: ''
      };
      setCustomFields(prev => [...prev, newField]);
    } catch (error) {
      console.error('❌ Error adding custom field:', error);
      alert('Failed to add custom field. Please try again.');
    }
  };

  // Handle updating custom field value
  const handleUpdateCustomField = (id: string, value: string) => {
    setCustomFields(prev => 
      prev.map(field => field.id === id ? { ...field, value } : field)
    );
  };

  // Handle removing a custom field definition
  const handleRemoveCustomField = async (id: string) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      console.log('🗑️ Removing custom field from database:', id);
      
      // Delete from database
      const { error } = await supabase
        .from('opportunity_custom_fields')
        .delete()
        .eq('id', id)
        .eq('business_id', selectedBusiness.id);

      if (error) {
        console.error('❌ Error removing custom field:', error);
        alert('Failed to remove custom field. Please try again.');
        return;
      }

      console.log('✅ Custom field removed from database');

      // Remove from local state
      setCustomFieldDefinitions(prev => prev.filter(def => def.id !== id));
      
      // Remove from current fields
      setCustomFields(prev => prev.filter(field => field.id !== id));
    } catch (error) {
      console.error('❌ Error removing custom field:', error);
      alert('Failed to remove custom field. Please try again.');
    }
  };

  // Reset modal state when closing
  const handleModalClose = () => {
    setIsOpportunityModalOpen(false);
    setSelectedCustomer(null);
    setOpportunityTitle('');
    setSelectedPipeline(null);
    setSelectedStage(null);
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    setOpportunityStatus('Open');
    setSelectedTags([]);
    // Reset custom field values but keep the definitions
    initializeCustomFields();
  };

  // Mark opportunity as read
  const markOpportunityAsRead = async (opportunityId: number) => {
    if (!supabase || !selectedBusiness?.id) return;

    try {
      // Immediately update the badge count via context
      markAsRead(opportunityId);

      const { error } = await supabase
        .from('pipeline_opportunities')
        .update({ is_unread: false })
        .eq('id', opportunityId)
        .eq('business_id', selectedBusiness.id);

      if (error) throw error;

      // Update local state
      setOpportunities(prevOpportunities => 
        prevOpportunities.map(opp => 
          opp.id === opportunityId 
            ? { ...opp, is_unread: false }
            : opp
        )
      );
    } catch (error) {
      console.error('Error marking opportunity as read:', error);
    }
  };

  // Handle opening opportunity info modal
  const handleOpenOpportunityInfo = async (opportunity: Opportunity) => {
    try {
      // Mark opportunity as read if it's unread
      if (opportunity.is_unread) {
        await markOpportunityAsRead(opportunity.id);
      }
      
      setSelectedOpportunityForInfo(opportunity);
      
      // Find the customer for this opportunity
      if (!supabase) return;
      const { data: customerData } = await supabase
        .from('customers')
        .select('*')
        .eq('id', opportunity.customer_id)
        .single();
      
      // Find the pipeline and stage
      const pipeline = pipelines.find(p => p.id === opportunity.pipeline_id);
      const stage = pipeline?.stages?.find(s => s.id === opportunity.stage_id);
      
      // Parse custom fields if they exist and merge with current definitions
      let mergedCustomFields = [...customFieldDefinitions.map(def => ({
        id: def.id,
        name: def.name,
        type: def.type,
        value: ''
      }))];
      
      if (opportunity.custom_fields) {
        try {
          const savedFields = JSON.parse(opportunity.custom_fields);
          // Merge saved values with current definitions
          mergedCustomFields = mergedCustomFields.map(field => {
            const savedField = savedFields.find((saved: any) => saved.id === field.id || saved.name === field.name);
            return savedField ? { ...field, value: savedField.value } : field;
          });
        } catch (error) {
          console.error('Error parsing custom fields:', error);
        }
      }
      
      // Populate the form fields with opportunity data
      setSelectedCustomer(customerData || null);
      setOpportunityTitle(opportunity.title);
      setSelectedPipeline(pipeline || null);
      setSelectedStage(stage || null);
      setOpportunityValue(opportunity.value);
      setBusinessName(opportunity.business_name || '');
      setOpportunitySource(opportunity.source || '');
      setOpportunityStatus(opportunity.status || 'Open');
      
      // Parse and set tags
      try {
        const parsedTags = opportunity.tags ? JSON.parse(opportunity.tags) : [];
        setSelectedTags(Array.isArray(parsedTags) ? parsedTags : []);
      } catch (error) {
        console.error('Error parsing tags:', error);
        setSelectedTags([]);
      }
      
      setCustomFields(mergedCustomFields);
      
      // Open the info modal
      setIsOpportunityInfoModalOpen(true);
    } catch (error) {
      console.error('Error loading opportunity info:', error);
    }
  };

  // Handle closing opportunity info modal
  const handleCloseOpportunityInfo = () => {
    setIsOpportunityInfoModalOpen(false);
    setSelectedOpportunityForInfo(null);
    // Reset all form fields
    setSelectedCustomer(null);
    setOpportunityTitle('');
    setSelectedPipeline(null);
    setSelectedStage(null);
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    setOpportunityStatus('Open');
    setSelectedTags([]);
    // Reset custom field values but keep the definitions
    initializeCustomFields();
  };

  // Handle updating an existing opportunity
  const handleUpdateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCustomer || !selectedPipeline || !selectedStage || !selectedOpportunityForInfo || !supabase) {
      console.log('Update validation failed');
      return;
    }

    try {
      console.log('Updating opportunity in database...');
      setIsLoading(true);
      
      const updateData = {
        customer_id: selectedCustomer.id,
        title: opportunityTitle,
        pipeline_id: selectedPipeline.id,
        stage_id: selectedStage.id,
        value: opportunityValue,
        business_name: businessName || null,
        source: opportunitySource || null,
        status: opportunityStatus,
        tags: selectedTags.length > 0 ? JSON.stringify(selectedTags) : null,
        custom_fields: customFields.length > 0 ? JSON.stringify(customFields) : null,
        updated_at: new Date().toISOString()
      };
      
      console.log('Update data:', updateData);
      
      // Update the opportunity in database
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .update(updateData)
        .eq('id', selectedOpportunityForInfo.id)
        .select()
        .single();

      console.log('Update response:', { data, error });

      if (error) throw error;

      // Update the opportunity in local state
      setOpportunities(prev => 
        prev.map(opp => opp.id === selectedOpportunityForInfo.id ? data : opp)
      );
      
      console.log('Opportunity updated successfully:', data);
      
      // Close the modal
      handleCloseOpportunityInfo();
      
    } catch (err) {
      console.error('Error updating opportunity:', err);
      setError('Failed to update opportunity. Please try again.');
      alert('Error updating opportunity: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle deleting an opportunity
  const handleDeleteOpportunity = async () => {
    if (!selectedOpportunityForInfo || !supabase) {
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete the opportunity "${selectedOpportunityForInfo.title}"? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      console.log('Deleting opportunity from database...');
      setIsLoading(true);
      
      // Delete the opportunity from database
      const { error } = await supabase
        .from('pipeline_opportunities')
        .delete()
        .eq('id', selectedOpportunityForInfo.id);

      console.log('Delete response error:', error);

      if (error) throw error;

      // Remove the opportunity from local state
      setOpportunities(prev => 
        prev.filter(opp => opp.id !== selectedOpportunityForInfo.id)
      );
      
      console.log('Opportunity deleted successfully');
      
      // Close the modal
      handleCloseOpportunityInfo();
      
    } catch (err) {
      console.error('Error deleting opportunity:', err);
      setError('Failed to delete opportunity. Please try again.');
      alert('Error deleting opportunity: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle opening SMS for a customer
  const handleOpenSMS = async (opportunity: Opportunity) => {
    if (!opportunity.customer_id) {
      console.warn('No customer ID found for opportunity:', opportunity);
      return;
    }

    try {
      // Navigate to messages page with customer ID as query parameter
      navigate(`/messages?customerId=${opportunity.customer_id}`);
    } catch (error) {
      console.error('Error navigating to messages:', error);
    }
  };

  // Handle deleting an opportunity directly from the card
  const handleDeleteOpportunityFromCard = async (opportunity: Opportunity) => {
    if (!supabase) {
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete the opportunity "${opportunity.title}"? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      console.log('Deleting opportunity from card...');
      setIsLoading(true);
      
      // Delete the opportunity from database
      const { error } = await supabase
        .from('pipeline_opportunities')
        .delete()
        .eq('id', opportunity.id);

      console.log('Delete response error:', error);

      if (error) throw error;

      // Remove the opportunity from local state
      setOpportunities(prev => 
        prev.filter(opp => opp.id !== opportunity.id)
      );
      
      console.log('Opportunity deleted successfully from card');
      
    } catch (err) {
      console.error('Error deleting opportunity:', err);
      setError('Failed to delete opportunity. Please try again.');
      alert('Error deleting opportunity: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Mobile detection and resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load data on component mount
  useEffect(() => {
    if (selectedBusiness?.id) {
      fetchPipelines();
      fetchOpportunities();
      fetchTags();
      loadCustomFieldDefinitions();
    }
  }, [selectedBusiness?.id]);

  // Set default pipeline when pipelines load
  useEffect(() => {
    if (pipelines.length > 0 && !opportunitiesViewPipeline) {
      setOpportunitiesViewPipeline(pipelines[0]);
    }
  }, [pipelines, opportunitiesViewPipeline]);

  // Handle status filter toggle
  const handleStatusToggle = (status: string) => {
    console.log('handleStatusToggle called with:', status); // Debug log
    console.log('Current selectedStatuses:', selectedStatuses); // Debug log
    setSelectedStatuses(prev => {
      const newStatuses = prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status];
      console.log('New selectedStatuses:', newStatuses); // Debug log
      return newStatuses;
    });
  };

  // Calculate dropdown position
  const calculateDropdownPosition = () => {
    if (statusButtonRef.current) {
      const rect = statusButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Handle status filter open/close
  const handleStatusFilterToggle = () => {
    if (!isStatusFilterOpen) {
      calculateDropdownPosition();
      setIsStatusFilterOpen(true);
    } else {
      setIsStatusFilterOpen(false);
      setDropdownPosition(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isStatusFilterOpen) {
        const target = event.target as Node;
        const isInsideButton = statusButtonRef.current?.contains(target);
        const isInsideDropdown = dropdownRef.current?.contains(target);
        
        if (!isInsideButton && !isInsideDropdown) {
          setIsStatusFilterOpen(false);
          setDropdownPosition(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusFilterOpen]);

  // Mobile swipe handling functions
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !isDragging) return;
    
    const currentX = e.targetTouches[0].clientX;
    const diff = currentX - touchStart;
    
    // Limit drag distance to prevent over-scrolling
    const maxDrag = window.innerWidth * 0.8;
    const limitedDiff = Math.max(-maxDrag, Math.min(maxDrag, diff));
    
    setTouchEnd(currentX);
    setDragOffset(limitedDiff);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || isAnimating) return;
    
    setIsDragging(false);
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (opportunitiesViewPipeline?.stages) {
      const maxIndex = opportunitiesViewPipeline.stages.length - 1;
      
      if (isLeftSwipe && currentStageIndex < maxIndex) {
        // Animate to complete the swipe
        setIsAnimating(true);
        setSlideDirection('left');
        setTimeout(() => {
          setCurrentStageIndex(prev => prev + 1);
          setDragOffset(0);
          setSlideDirection(null);
          setTimeout(() => setIsAnimating(false), 50);
        }, 150);
      } else if (isRightSwipe && currentStageIndex > 0) {
        // Animate to complete the swipe
        setIsAnimating(true);
        setSlideDirection('right');
        setTimeout(() => {
          setCurrentStageIndex(prev => prev - 1);
          setDragOffset(0);
          setSlideDirection(null);
          setTimeout(() => setIsAnimating(false), 50);
        }, 150);
      } else {
        // Snap back to original position
        setIsAnimating(true);
        setTimeout(() => {
          setDragOffset(0);
          setTimeout(() => setIsAnimating(false), 200);
        }, 50);
      }
    } else {
      // Reset if no pipeline
      setDragOffset(0);
    }
  };

  // Reset stage index when pipeline changes
  React.useEffect(() => {
    setCurrentStageIndex(0);
  }, [opportunitiesViewPipeline]);

  return (
    <div className="min-h-screen bg-white border-0" data-crm-page style={{ border: 'none', outline: 'none' }}>
      <div className="w-full px-6 py-6" style={{ border: 'none', outline: 'none' }}>
        <div className="space-y-6">
           {/* Header */}
           <div className={`${isMobile ? 'space-y-4' : 'flex items-center justify-between'}`}>
             <div className={`${isMobile ? 'flex items-center justify-between' : 'flex items-center gap-4'}`}>
               <h1 className="text-2xl font-semibold text-gray-900">
                 {activeTab === 'opportunities' ? 'Deals' : 'Pipelines'}
               </h1>
               {!isMobile && activeTab === 'opportunities' && (
                 <div className="relative">
                   <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                   <input
                     type="text"
                     placeholder="Search deals"
                     className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                   />
                 </div>
               )}
               {isMobile && (
                 <div className="flex items-center gap-2">
                   {activeTab === 'opportunities' && (
                     <>
                       <button 
                         ref={statusButtonRef}
                         onClick={handleStatusFilterToggle}
                         className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                         disabled={isLoadingStatusFilter}
                       >
                         <Filter className="h-5 w-5" />
                       </button>
                       <button 
                         onClick={() => setActiveTab('pipelines')}
                         className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                       >
                         <Settings className="h-5 w-5" />
                       </button>
                     </>
                   )}
                   {activeTab === 'pipelines' && (
                     <button 
                       onClick={() => setActiveTab('opportunities')}
                       className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                     >
                       <TrendingUp className="h-5 w-5" />
                     </button>
                   )}
                   
                   {/* Create Button - Mobile */}
                   <button 
                     onClick={() => activeTab === 'opportunities' ? handleOpenOpportunityModal(opportunitiesViewPipeline || undefined) : handleOpenModal()}
                     className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                   >
                     {activeTab === 'opportunities' ? 'Create Deal' : 'Create Pipeline'}
                   </button>
                 </div>
               )}
             </div>
             
             {/* Mobile Search Bar */}
             {isMobile && activeTab === 'opportunities' && (
               <div className="relative">
                 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                 <input
                   type="text"
                   placeholder="Search deals"
                   className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                 />
               </div>
             )}
             
             {!isMobile && (
               <div className="flex items-center gap-3">
                 {activeTab === 'opportunities' && (
                   <>
                     <button 
                       ref={statusButtonRef}
                       onClick={handleStatusFilterToggle}
                       className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                       disabled={isLoadingStatusFilter}
                     >
                       <Filter className="h-4 w-4" />
                     </button>
                     <button 
                       onClick={() => setActiveTab('pipelines')}
                       className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                     >
                       <Settings className="h-4 w-4" />
                       <span>Pipelines</span>
                     </button>
                   </>
                 )}
                 {activeTab === 'pipelines' && (
                   <button 
                     onClick={() => setActiveTab('opportunities')}
                     className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                   >
                     <TrendingUp className="h-4 w-4" />
                     <span>Deals</span>
                   </button>
                 )}
                 {/* Create Button - Desktop Only */}
                 <button 
                   onClick={() => activeTab === 'opportunities' ? handleOpenOpportunityModal(opportunitiesViewPipeline || undefined) : handleOpenModal()}
                   className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                 >
                   {activeTab === 'opportunities' ? 'Create Deal' : 'Create Pipeline'}
                 </button>
               </div>
             )}
           </div>
      
      {/* Page content */}
        {activeTab === 'opportunities' && (
            <div className="space-y-6">
            
              {/* Kanban Board */}
              <div className="bg-white overflow-hidden">
                <div className="">
                  {pipelines.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center max-w-md">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Building className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pipelines Created</h3>
                        <p className="text-gray-500 text-sm mb-6">
                          Create a pipeline to start managing your opportunities and track your sales process.
                        </p>
                        <button 
                          onClick={() => setActiveTab('pipelines')}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                        >
                          Create Your First Pipeline
                        </button>
                      </div>
                    </div>
                                     ) : !opportunitiesViewPipeline || !opportunitiesViewPipeline.stages || opportunitiesViewPipeline.stages.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center max-w-md">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Stages in Pipeline</h3>
                        <p className="text-gray-500 text-sm mb-6">
                          This pipeline doesn't have any stages. Edit the pipeline to add stages and start tracking opportunities.
                        </p>
                        <button 
                          onClick={() => setActiveTab('pipelines')}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                        >
                          Edit Pipeline
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Kanban Board */
                    <div className={isMobile ? '' : 'overflow-x-auto -mx-8 px-8'}>
                      {isMobile ? (
                        /* Mobile Swipe View - One Stage at a Time */
                        <div 
                          className="relative"
                          onTouchStart={onTouchStart}
                          onTouchMove={onTouchMove}
                          onTouchEnd={onTouchEnd}
                        >
                           
                           {/* Stage Dots Indicator - Updated Design */}
                           <div className="flex justify-center space-x-2 mb-6">
                             {opportunitiesViewPipeline.stages?.map((_, index) => (
                               <button
                                 key={index}
                                 onClick={() => {
                                   if (index !== currentStageIndex && !isAnimating && !isDragging) {
                                     setIsAnimating(true);
                                     setSlideDirection(index > currentStageIndex ? 'left' : 'right');
                                     setTimeout(() => {
                                       setCurrentStageIndex(index);
                                       setDragOffset(0);
                                       setSlideDirection(null);
                                       setTimeout(() => setIsAnimating(false), 50);
                                     }, 150);
                                   }
                                 }}
                                 className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                   index === currentStageIndex 
                                     ? 'bg-blue-500 scale-110' 
                                     : 'bg-gray-300 hover:bg-gray-400'
                                 }`}
                               />
                             ))}
                           </div>
                          
                          {/* Current Stage Content with Real-time Drag Animation */}
                          <div className="overflow-hidden">
                            <div 
                              className={`${isDragging ? '' : 'transition-all duration-300 ease-out'} ${isAnimating && !isDragging ? 'duration-200' : ''}`}
                              style={{
                                transform: isDragging 
                                  ? `translateX(${dragOffset}px)` 
                                  : slideDirection === 'left' 
                                  ? 'translateX(-100%)' 
                                  : slideDirection === 'right' 
                                  ? 'translateX(100%)' 
                                  : 'translateX(0%)',
                                opacity: isDragging ? Math.max(0.7, 1 - Math.abs(dragOffset) / (window.innerWidth * 0.5)) : 1,
                              }}
                            >
                              {opportunitiesViewPipeline.stages?.[currentStageIndex] && (() => {
                                const stage = opportunitiesViewPipeline.stages![currentStageIndex];
                                const stageOpportunities = opportunities.filter(opp => 
                                  opp.pipeline_id === opportunitiesViewPipeline.id && 
                                  opp.stage_id === stage.id &&
                                  selectedStatuses.includes(opp.status)
                                );
                                
                               // Calculate total value for this stage
                               const totalValue = stageOpportunities.reduce((sum, opp) => sum + (opp.value || 0), 0);
                               
                               return (
                                 <div className="mx-4">
                               {/* Stage Container - Match Desktop Design */}
                               <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                 <div className="flex items-center justify-between mb-4">
                                   <div>
                                     <h3 className="font-semibold text-gray-900 text-lg">{stage.name}</h3>
                                     <div className="text-sm text-gray-600 mt-1 font-medium">
                                       ${totalValue.toLocaleString()} • {stageOpportunities.length} deals
                                     </div>
                                   </div>
                                   <button className="text-gray-400 hover:text-gray-600">
                                     <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                       <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                                     </svg>
                                   </button>
                                 </div>
                                 
                                 {/* Opportunity Cards */}
                                 {stageOpportunities.length > 0 ? (
                                   <div className="space-y-3">
                                     {stageOpportunities.map(opportunity => (
                                     <div 
                                       key={opportunity.id} 
                                       className="bg-white rounded-lg border border-gray-200 p-4 relative cursor-pointer hover:shadow-md transition-all duration-200 group"
                                       onClick={() => handleOpenOpportunityInfo(opportunity)}
                                     >
                                       {/* Unread indicator */}
                                       {opportunity.is_unread && (
                                         <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full border-2 border-white z-10"></div>
                                       )}
                                       
                                       {/* Opportunity Title */}
                                       <h4 className="font-medium text-gray-900 mb-3 text-sm leading-tight pr-4">{opportunity.title}</h4>
                                       
                                       {/* Deal Value */}
                                       <div className="text-lg font-semibold text-gray-900 mb-4">
                                         ${(opportunity.value || 0).toLocaleString()}
                                       </div>
                                       
                                       {/* Customer Name and Icon at Bottom */}
                                       <div className="pt-2 border-t border-gray-100">
                                         <div className="flex items-center gap-2 mb-2">
                                           <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                                             <span className="text-xs font-medium text-gray-700">
                                               {opportunity.customer_name ? opportunity.customer_name.charAt(0).toUpperCase() : 'U'}
                                             </span>
                                           </div>
                                           <span className="text-sm text-gray-600">
                                             {opportunity.customer_name || 'Unknown Contact'}
                                           </span>
                                         </div>
                                         
                                         {/* Quick Actions */}
                                         <div className="flex items-center gap-2">
                                           <button 
                                             className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-150"
                                             title="Send SMS"
                                             onClick={(e) => {
                                               e.stopPropagation();
                                               handleOpenSMS(opportunity);
                                             }}
                                           >
                                             <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                               <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
                                             </svg>
                                           </button>
                                           <button 
                                             className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors duration-150"
                                             title="Call"
                                             onClick={(e) => e.stopPropagation()}
                                           >
                                             <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                               <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                                             </svg>
                                           </button>
                                         </div>
                                       </div>
                                     </div>
                                     ))}
                                   </div>
                                 ) : (
                                   <div className="text-center py-8">
                                     <div className="text-gray-500 text-sm">No opportunities in this stage</div>
                                   </div>
                                 )}
                               </div>
                                 </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Desktop Horizontal Scroll View */
                        <div className="flex space-x-6 pb-4 overflow-x-auto">
                        {opportunitiesViewPipeline.stages.map(stage => {
                          // Filter opportunities for this stage and selected statuses
                          const stageOpportunities = opportunities.filter(opp => 
                            opp.pipeline_id === opportunitiesViewPipeline.id && 
                            opp.stage_id === stage.id &&
                            selectedStatuses.includes(opp.status)
                          );
                          
                          // Calculate total value for this stage
                          const totalValue = stageOpportunities.reduce((sum, opp) => sum + (opp.value || 0), 0);
                          
                          return (
                            <div key={stage.id} className="flex-shrink-0 w-80 bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-4">
                                <div>
                                  <h3 className="font-semibold text-gray-900 text-lg">{stage.name}</h3>
                                  <div className="text-sm text-gray-600 mt-1 font-medium">
                                    ${totalValue.toLocaleString()} • {stageOpportunities.length} deals
                                  </div>
                                </div>
                                <button className="text-gray-400 hover:text-gray-600">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                                  </svg>
                                </button>
                              </div>
                              
                              {/* Opportunity Cards */}
                              <div className="space-y-3">
                                {stageOpportunities.map(opportunity => (
                                  <div 
                                    key={opportunity.id} 
                                    className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-sm transition-shadow duration-200 group relative"
                                    onClick={() => handleOpenOpportunityInfo(opportunity)}
                                  >
                                    {/* Unread indicator */}
                                    {opportunity.is_unread && (
                                      <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></div>
                                    )}
                                    
                                    {/* Deal Title */}
                                    <div className="flex items-start justify-between mb-3">
                                      <h4 className="font-medium text-gray-900 text-sm leading-tight pr-4">{opportunity.title}</h4>
                                      <button 
                                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all duration-200"
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          if (opportunity.is_unread) {
                                            await markOpportunityAsRead(opportunity.id);
                                          }
                                          handleDeleteOpportunityFromCard(opportunity);
                                        }}
                                        title="Delete opportunity"
                                        disabled={isLoading}
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                    
                                    {/* Deal Value */}
                                    <div className="text-lg font-semibold text-gray-900 mb-4">
                                      ${(opportunity.value || 0).toLocaleString()}
                                    </div>
                                    
                                    {/* Customer Name and Icon at Bottom */}
                                    <div className="pt-2 border-t border-gray-100">
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                                          <span className="text-xs font-medium text-gray-700">
                                            {opportunity.customer_name ? opportunity.customer_name.charAt(0).toUpperCase() : 'U'}
                                          </span>
                                        </div>
                                        <span className="text-sm text-gray-600">
                                          {opportunity.customer_name || 'Unknown Contact'}
                                        </span>
                                      </div>
                                      
                                      {/* Quick Actions */}
                                      <div className="flex items-center gap-2">
                                        <button 
                                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-150"
                                          title="Send SMS"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenSMS(opportunity);
                                          }}
                                        >
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
                                          </svg>
                                        </button>
                                        <button 
                                          className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors duration-150"
                                          title="Call"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {opportunities.length === 0 && pipelines.length > 0 && opportunitiesViewPipeline?.stages && opportunitiesViewPipeline.stages.length > 0 && (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center max-w-md">
                        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center">
                          <Users className="h-10 w-10 text-blue-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">No Opportunities Match the Filters</h3>
                        <p className="text-gray-600 leading-relaxed mb-6">
                          Try adjusting your filters to find the opportunities you're looking for.
                        </p>

                      </div>
              </div>
            )}
                </div>
              </div>
          </div>
        )}
        
        {activeTab === 'pipelines' && (
            <div className="space-y-6">
            
            {error && (
                <div className="bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-xl">
                      <X className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-red-800">Error</h3>
                      <p className="text-sm text-red-600 mt-1">{error}</p>
                    </div>
                  </div>
              </div>
            )}
            
            {isLoading && !isModalOpen ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="relative">
                      <div className="w-12 h-12 mx-auto mb-4">
                        <div className="absolute inset-0 rounded-full border-4 border-green-100"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-green-600 border-t-transparent animate-spin"></div>
                      </div>
                    </div>
                    <p className="text-gray-600">Loading pipelines...</p>
                  </div>
              </div>
            ) : pipelines.length === 0 ? (
                <div className="bg-white/70 backdrop-blur-xl border border-white/20 rounded-2xl p-12 shadow-xl text-center">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl flex items-center justify-center">
                      <TrendingUp className="h-10 w-10 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">No Pipelines Found</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      Create a new pipeline to get started with managing your sales opportunities.
                    </p>
                    <button 
                      onClick={() => handleOpenModal()}
                      className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-150 shadow-lg hover:shadow-xl font-semibold"
                    >
                      Create Your First Pipeline
                    </button>
                  </div>
              </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pipeline Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pipelines.map((pipeline) => (
                          <tr key={pipeline.id} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <TrendingUp className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{pipeline.pipeline_name}</div>
                                  {pipeline.stages && (
                                    <div className="text-sm text-gray-500 mt-1">
                                      {pipeline.stages.length} stages
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-2">
                                <button 
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenModal(true, pipeline);
                                  }}
                                  title="Edit pipeline"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button 
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePipeline(pipeline.id);
                                  }}
                                  title="Delete pipeline"
                                >
                                  <Trash className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* Modals with glassmorphism styling */}
      {/* Create/Edit Pipeline Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-4 sm:px-6 py-4 sm:py-6 border-b border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/10 rounded-xl">
                    {isEditMode ? <Edit className="h-5 w-5 text-green-600" /> : <Plus className="h-5 w-5 text-green-600" />}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {isEditMode ? 'Edit Pipeline' : 'Add Pipeline'}
              </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {isEditMode ? 'Update pipeline details and stages' : 'Create a new sales pipeline'}
                    </p>
                  </div>
                </div>
              <button 
                onClick={handleCloseModal} 
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-150"
              >
                  <X className="h-5 w-5" />
              </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
              <div>
                <label htmlFor="pipeline-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Pipeline Name
                </label>
                <input
                  type="text"
                  id="pipeline-name"
                  placeholder="Enter pipeline name"
                  value={pipelineName}
                  onChange={(e) => setPipelineName(e.target.value)}
                  className="w-full px-4 py-3 text-sm bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-300 transition-all duration-200"
                  required
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700">
                      Pipeline Stages
                  </label>
                    <p className="text-xs text-gray-500 mt-1">Drag to reorder stages</p>
                  </div>
                  <div className="text-xs font-medium text-gray-500">
                    Actions
                  </div>
                </div>

                <div className="space-y-3">
                {stages
                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                  .map((stage, index) => (
                  <div 
                    key={stage.id} 
                      className="group bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl p-3 transition-all duration-200 hover:bg-white/80"
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, stage.id)}
                    onDragOver={(e) => handleDragOver(e, stage.id)}
                    onDragEnd={handleDragEnd}
                  >
                      <div className="flex items-center gap-3">
                        <div className="cursor-grab text-gray-400 hover:text-gray-600 p-1" title="Drag to reorder">
                          <GripVertical className="h-4 w-4" />
                    </div>
                    <input
                      type="text"
                      placeholder="Stage Name"
                      value={stage.name}
                      onChange={(e) => updateStageName(stage.id, e.target.value)}
                          className="flex-1 px-3 py-2 text-sm bg-white/80 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-300"
                        />
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button 
                            type="button" 
                            className="p-2 text-green-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-150"
                            title="Analytics"
                          >
                            <PieChart className="h-4 w-4" />
                      </button>
                          <button 
                            type="button" 
                            className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                            title="Filter"
                          >
                            <Filter className="h-4 w-4" />
                      </button>
                      <button 
                        type="button" 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeStage(stage.id);
                        }}
                            className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                        disabled={stages.length <= 1}
                            title="Delete stage"
                      >
                            <Trash className="h-4 w-4" />
                      </button>
                        </div>
                    </div>
                  </div>
                ))}
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    addStage();
                  }}
                  className="mt-4 flex items-center gap-2 text-green-600 hover:text-green-700 text-sm font-medium"
                >
                  <Plus className="h-4 w-4" /> Add Stage
                </button>
              </div>
            </form>

            <div className="px-4 sm:px-6 py-4 sm:py-6 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-white/80 transition-all duration-150 w-full sm:w-auto"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="pipeline-form"
                  onClick={handleSubmit}
                  className={`px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-150 shadow-lg hover:shadow-xl w-full sm:w-auto ${
                    isLoading ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2 justify-center">
                      <Loader className="h-4 w-4 animate-spin" />
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </div>
                  ) : (
                    isEditMode ? 'Update Pipeline' : 'Create Pipeline'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Opportunity Modal */}
      {isOpportunityModalOpen && createPortal(
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 ${
            isOpportunityModalOpen ? 'bg-opacity-50' : 'bg-opacity-0'
          } z-[9999]`}
          onClick={handleModalClose}
          style={{
            transform: 'translateZ(0)',
            WebkitTransform: 'translateZ(0)',
            isolation: 'isolate',
            touchAction: 'manipulation',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}
        >
          <div 
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              isOpportunityModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleModalClose}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
                    </button>
                    <h2 className="text-xl font-semibold text-white">
                      Create Opportunity
                    </h2>
                  </div>
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                <form 
                  id="opportunity-form"
                  className="p-6 space-y-6" 
                  style={{ WebkitOverflowScrolling: 'touch' }}
                  onSubmit={handleCreateOpportunity}
                >
                  {/* Customer Selection Field */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Customer *
                    </label>
                    {selectedCustomer ? (
                      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                      
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 flex-1 min-w-0">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold text-gray-900 truncate">
                                {selectedCustomer.first_name} {selectedCustomer.last_name}
                              </div>
                              {selectedCustomer.email && (
                                <div className="text-sm text-gray-600 font-medium truncate">
                                  {selectedCustomer.email}
                                </div>
                              )}
                              {selectedCustomer.phone && (
                                <div className="text-sm text-gray-500 font-medium">
                                  {selectedCustomer.phone}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2 flex-shrink-0">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsCustomerModalOpen(true);
                              }}
                              className="px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              onClick={handleCustomerRemove}
                              className="px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsCustomerModalOpen(true);
                        }}
                        className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Select Customer
                      </button>
                    )}
                  </div>

                  {/* Opportunity Title Field */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opportunity Title *
                    </label>
                    <input
                      type="text"
                      placeholder="Enter opportunity title"
                      value={opportunityTitle}
                      onChange={(e) => setOpportunityTitle(e.target.value)}
                      className="w-full px-4 py-3 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>

                  {/* Pipeline Selection Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Pipeline
                    </label>
                    <div className="relative">
                      <select
                        value={selectedPipeline?.id || ''}
                        onChange={(e) => {
                          const pipeline = pipelines.find(p => p.id.toString() === e.target.value);
                          handlePipelineSelect(pipeline || null);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                      >
                        <option value="">Select a pipeline</option>
                        {pipelines.map(pipeline => (
                          <option key={pipeline.id} value={pipeline.id}>
                            {pipeline.pipeline_name}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Stage Selection Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Stage *
                    </label>
                    <div className="relative">
                      <select
                        value={selectedStage?.id || ''}
                        onChange={(e) => {
                          const stage = selectedPipeline?.stages?.find(s => s.id.toString() === e.target.value);
                          setSelectedStage(stage || null);
                        }}
                        disabled={!selectedPipeline || !selectedPipeline.stages || selectedPipeline.stages.length === 0}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">Select a stage</option>
                        {selectedPipeline?.stages?.map(stage => (
                          <option key={stage.id} value={stage.id}>
                            {stage.name}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                        </svg>
                      </div>
                    </div>
                    {!selectedPipeline && (
                      <p className="mt-1 text-sm text-gray-500">Please select a pipeline first</p>
                    )}
                  </div>

                  {/* Status Selection Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Status
                    </label>
                    <div className="relative">
                      <select
                        value={opportunityStatus}
                        onChange={(e) => setOpportunityStatus(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                      >
                        <option value="Open">Open</option>
                        <option value="Won">Won</option>
                        <option value="Lost">Lost</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Opportunity Value Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opportunity Value
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={opportunityValue}
                        onChange={(e) => setOpportunityValue(Number(e.target.value) || 0)}
                        className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Business Name Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Business Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter business name"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Opportunity Source Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opportunity Source
                    </label>
                    <input
                      type="text"
                      placeholder="Enter opportunity source"
                      value={opportunitySource}
                      onChange={(e) => setOpportunitySource(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Tags Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Tags
                    </label>
                    
                    {/* Selected Tags Display */}
                    {selectedTags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {selectedTags.map(tag => (
                          <span
                            key={tag.id}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag.name}
                            <button
                              type="button"
                              onClick={() => handleTagRemove(tag.id)}
                              className="ml-2 text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Tag Selection Dropdown */}
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <select
                          onChange={(e) => {
                            const selectedTag = availableTags.find(tag => tag.id === e.target.value);
                            if (selectedTag) {
                              handleTagSelect(selectedTag);
                              e.target.value = ''; // Reset dropdown
                            }
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          defaultValue=""
                        >
                          <option value="">Select existing tag...</option>
                          {availableTags
                            .filter(tag => !selectedTags.find(st => st.id === tag.id))
                            .map(tag => (
                            <option key={tag.id} value={tag.id}>
                              {tag.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Add New Tag Button */}
                      <button
                        type="button"
                        onClick={() => setIsNewTagModalOpen(true)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Add new tag"
                      >
                        +
                      </button>
                    </div>
                    
                    <p className="mt-1 text-xs text-gray-500">Select from existing tags or create new ones</p>
                  </div>

                  {/* Custom Fields Display */}
                  {customFieldDefinitions.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Custom Fields
                      </label>
                      <div className="space-y-3">
                        {customFields.map(field => (
                          <div key={field.id}>
                            <div className="flex items-center justify-between mb-1">
                              <label className="block text-xs font-medium text-gray-600">
                                {field.name}
                              </label>
                              <button
                                type="button"
                                onClick={() => handleRemoveCustomField(field.id)}
                                className="text-red-500 hover:text-red-700 text-xs"
                                title="Remove this custom field"
                              >
                                Remove
                              </button>
                            </div>
                            {field.type === 'text' && (
                              <input
                                type="text"
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                              />
                            )}
                            {field.type === 'number' && (
                              <input
                                type="number"
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                              />
                            )}
                            {field.type === 'textarea' && (
                              <textarea
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Enter ${field.name.toLowerCase()}`}
                                rows={2}
                              />
                            )}
                            {field.type === 'date' && (
                              <input
                                type="date"
                                value={field.value}
                                onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </form>
              
              {/* Footer */}
              <div className="bg-white border-t border-gray-200 px-6 py-4">
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleModalClose}
                    className="px-6 py-3 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="opportunity-form"
                    disabled={!selectedCustomer || !selectedPipeline || !selectedStage}
                    className="px-6 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Create Opportunity
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>, 
        document.body
      )}
    </div>
  );
}
